import Button from "@/components/Button";
import { CloseIcon } from "@/components/features/SquadBuilder/RightPanel/PanelHeader/PanelHeader";
import { goToAnotherPage } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { setShowFooter } from "@/redux/globalState/globalStateSlice";
import { shouldShowAdsSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

export type TacticsSidePanelOption = "filters" | "chosenTactic";

const sidebarOptionsLabel = {
  filters: "Filters",
  insertEACode: "Insert EA Code",
  chosenTactic: "Tactic Template",
};

interface TacticsSidebarProps {
  children: React.ReactNode;
  sidebarOption: TacticsSidePanelOption;
  scrollIntoBottom: boolean;
  handleCloseSidebar: () => void;
  handleBuildSquadWithTactic: () => void;
}

const TacticsSidebar = ({
  sidebarOption,
  children,
  scrollIntoBottom,
  handleCloseSidebar,
  handleBuildSquadWithTactic,
}: TacticsSidebarProps) => {
  const dispatch = useDispatch();
  const scrollPosition = useRef(0);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const shouldShowAds = useSelector(shouldShowAdsSelector);

  useEffect(() => {
    if (isMobile) {
      dispatch(setShowFooter(false));
      scrollPosition.current = window.scrollY;
      window.scrollTo(0, 0);
    }
    return () => {
      if (isMobile) {
        dispatch(setShowFooter(true));
        window.scrollTo(0, scrollPosition.current);
      }
    };
  }, [isMobile]);

  return (
    <article
      className={cn(
        "flex flex-col w-full shrink-0 desktop:w-[278px] touch-device:rounded-lg desktop:mb-2 bg-panel-background shadow-side-panel desktop:overflow-y-auto absolute mobile:static z-20 mobile:pb-0 min-h-50 mobile:h-auto top-0",
        shouldShowAds ? "pb-54" : "pb-32",
      )}
    >
      <section className="flex bg-primary-dark mobile:bg-transparent border-t border-b border-gray-800 mobile:border-none flex-row items-center justify-between h-12 px-4 mobile:px-0">
        <div className="w-8" /> {/* For positioning */}
        <p className="text-sm text-white font-semibold desktop:font-normal">
          {sidebarOptionsLabel[sidebarOption]}
        </p>
        <button
          className="mobile:p-2 p-4 -m-4 mobile:m-0"
          onClick={handleCloseSidebar}
        >
          <CloseIcon />
        </button>
      </section>
      {children}
      <div className={cn(!scrollIntoBottom && "h-14")} />
      <div
        className={cn(
          "h-16 bg-white/20 w-full desktop:w-[278px] fixed mobile:bottom-0 mt-auto items-center justify-center",
          scrollIntoBottom
            ? "mobile:flex mobile:relative"
            : "fixed shadow-fixed-scroll",
          shouldShowAds ? "bottom-26" : "bottom-12",
        )}
        onClick={handleCloseSidebar}
      >
        <div
          className={cn(
            "absolute flex items-center justify-center inset-0 mx-auto",
            !scrollIntoBottom && "inset-y-1/2",
          )}
        >
          <Button
            variant="primary"
            label={
              sidebarOption === "chosenTactic"
                ? "Build Squad with Tactic"
                : "OK"
            }
            onClick={
              sidebarOption === "chosenTactic"
                ? () => handleBuildSquadWithTactic()
                : () => handleCloseSidebar()
            }
            {...(sidebarOption === "chosenTactic" && {
              icon: goToAnotherPage,
            })}
          />
        </div>
      </div>
    </article>
  );
};

export default TacticsSidebar;
